import ReactGA from 'react-ga'
import { t } from '@lingui/macro'

import dayjs from 'shared/dayjs'
import {
  ActivityHistoryResourceSourceEnum,
  ActivityHistoryRowType,
  ActivityHistoryTypeEnum,
  ActivityTableSelectionState,
  CPDSelectedItem
} from './activityHistoryTypes'
import {
  CpdBatchItemInput,
  CpdUserEntryItemType,
  Maybe,
  WorkbookStatusEnum
} from '../../graphql/private/PrivateSchemaTypes'
import {
  ACTIVITY_HISTORY_STATUSES,
  ACTIVITY_HISTORY_TOPIC_STATUES
} from './activityHistoryConstants'
import {
  ActivityCpdDataFragment,
  ActivityHistoryQuery,
  ActivityTopicDataFragment
} from './ActivityHistory.codegen'
import { linkBuilder } from 'shared/linkBuilder'
import { getPreselectedDateRange } from '../../components/utils/predefinedDateRanges'
import { getDateFormatBasedOnLocale } from 'front-end/src/components/common/date-time-pickers/dateTimePickersUtils'

export const getActivityFormattedName = (row: ActivityHistoryRowType) => {
  switch (row.__typename) {
    case ActivityHistoryTypeEnum.CPDManualEntryModel:
      return row.name || ''
    case ActivityHistoryTypeEnum.TopicEnrolmentModelAsStudent:
      return row?.topicVersion?.title || ''
    case ActivityHistoryTypeEnum.UserEventModel:
      return row?.event?.title || ''
    default:
      return ''
  }
}

const getActivityTopicStatus = (status?: WorkbookStatusEnum | null) => {
  return status ? ACTIVITY_HISTORY_TOPIC_STATUES[status] : ''
}

const getActivityEventStatus = ({
  startDate,
  endDate,
  isEventArchived
}: {
  startDate?: string | null
  endDate?: string | null
  isEventArchived: boolean
}) => {
  if (!startDate || !endDate) return ''

  const now = dayjs.utc()

  if (dayjs.utc(endDate).isBefore(now)) {
    return ACTIVITY_HISTORY_STATUSES.ATTENDED
  }
  if (dayjs.utc(startDate).isBefore(now)) {
    // It was decided to show ATTENDED status for event that is archived and did not finish yet.
    return isEventArchived
      ? ACTIVITY_HISTORY_STATUSES.ATTENDED
      : ACTIVITY_HISTORY_STATUSES.IN_PROGRESS
  }

  return ''
}

export const getActivityStatus = (row: ActivityHistoryRowType) => {
  switch (row.__typename) {
    case ActivityHistoryTypeEnum.CPDManualEntryModel:
      return ACTIVITY_HISTORY_STATUSES.COMPLETED
    case ActivityHistoryTypeEnum.TopicEnrolmentModelAsStudent:
      return getActivityTopicStatus(row.status)
    case ActivityHistoryTypeEnum.UserEventModel:
      return getActivityEventStatus({
        startDate: row.event.start_time,
        endDate: row.event.end_time,
        isEventArchived: !!row.event.deleted_at
      })

    default:
      return ''
  }
}

const getActivityDurationSeconds = (row: ActivityHistoryRowType) => {
  let duration: Maybe<number> | undefined = 0

  switch (row.__typename) {
    case 'CPDManualEntryModel':
      duration = row.numberOfSeconds
      break
    case 'UserEventModel':
      const startTime = row.event.start_time
      const endTime = row.event.end_time

      if (startTime && endTime) {
        const startTimeDayJs = dayjs(startTime)
        const endTimeDayJs = dayjs(endTime)

        duration = endTimeDayJs.diff(startTimeDayJs, 'seconds')
      }
      break
    case 'TopicEnrolmentModelAsStudent':
      duration = row.readDurationSeconds
      break
    default:
      break
  }

  return duration || 0
}

export const formatDurationForCSV = (row: ActivityHistoryRowType) => {
  const secondsDuration = getActivityDurationSeconds(row)
  const duration = dayjs.duration(secondsDuration, 'seconds')

  const zeroPad = (num) => String(num).padStart(2, '0')

  const h = Math.floor(duration.as('h'))
  // Remaining minutes = we have to remove hours
  const m = Math.floor(duration.as('m')) - h * 60
  // Reamining seconds = we have to remove hours and minutes
  const s = Math.floor(duration.as('s')) - m * 60 - h * 3600

  // Add leading zeros before returning
  return `${h}:${zeroPad(m)}:${zeroPad(s)}`
}

export const formatDuration = (row: ActivityHistoryRowType) => {
  const secondsDuration = getActivityDurationSeconds(row)

  const duration = dayjs.duration(secondsDuration, 'seconds')
  const hours = Math.floor(duration.as('h'))
  const minutes = Math.floor(duration.as('m'))
  const seconds = Math.floor(duration.as('s'))

  if (hours >= 1) {
    const remainingMinutes = Math.round(
      dayjs.duration(minutes - hours * 60, 'minutes').as('m')
    )
    const translatedMinutes =
      remainingMinutes > 0 ? t` ${remainingMinutes}m` : ''

    return t`${hours}h ${translatedMinutes}`
  }

  if (minutes >= 1) {
    if (seconds - minutes * 60 > 0) {
      // All other sections in the app are rounding seconds up to the nearest minute, we have to do it here too.
      // If there is some spare second, let's increase minute
      return t`${minutes + 1}m`
    }
    return t`${minutes}m`
  }

  return t`${seconds}s`
}

export const getActivityRowLink = (row: ActivityHistoryRowType) => {
  let link = ''

  switch (row.__typename) {
    case ActivityHistoryTypeEnum.TopicEnrolmentModelAsStudent:
      if (row.topic?.deleted_at || row.originSection?.deleted_at) {
        return ''
      }

      link = getActivityTopicLink(row.originSection, row.topic_id)
      break

    case ActivityHistoryTypeEnum.UserEventModel:
      if (row.event?.deleted_at) {
        return ''
      }

      link = linkBuilder.eventDetail(row.id)
      break
  }

  return `${window.location.origin}${link}`
}

export const formatActivityScore = (row: ActivityHistoryRowType) => {
  return checkIsTopic(row) && row.available_points
    ? `${row.auto_correct}/${row.available_points}`
    : ''
}

export const checkIsTopic = (
  row: ActivityHistoryRowType
): row is ActivityTopicDataFragment =>
  row.__typename === ActivityHistoryTypeEnum.TopicEnrolmentModelAsStudent

export const checkIsCPDEvidence = (
  row: ActivityHistoryRowType
): row is ActivityCpdDataFragment =>
  row.__typename === ActivityHistoryTypeEnum.CPDManualEntryModel

export const getActivityParentInfo = (row: ActivityHistoryRowType) => {
  switch (row.__typename) {
    case ActivityHistoryTypeEnum.TopicEnrolmentModelAsStudent:
      return getActivityTopicName(row.originSection)
    case ActivityHistoryTypeEnum.UserEventModel:
      return row.certification?.name || ''
    case ActivityHistoryTypeEnum.CPDManualEntryModel:
      return row.description || ''
    default:
      return ''
  }
}

export const getActivityTopicLink = (
  originSection: ActivityTopicDataFragment['originSection'],
  topicId: number
) => {
  if (!originSection) return ''

  switch (originSection.__typename) {
    case ActivityHistoryResourceSourceEnum.WorkspaceModelAsMember:
      return linkBuilder.topicUrl(topicId)
    case ActivityHistoryResourceSourceEnum.CertificationModelAsStudent:
      return linkBuilder.topicUrlViaCertification(originSection.id, topicId)
    case ActivityHistoryResourceSourceEnum.PathwayModelAsStudent:
      return linkBuilder.topicUrlViaPathway(originSection.id, topicId)
    default:
      return ''
  }
}

export const getActivityTopicName = (
  originSection: ActivityTopicDataFragment['originSection']
) => {
  if (originSection?.__typename === 'PathwayModelAsStudent')
    return originSection.title
  return originSection?.name || ''
}

export const logActivityCSVDownload = () => {
  ReactGA.event({
    category: 'Activity history',
    action: 'Click Download'
  })
}

export const logActivityDateFilter = ({
  from,
  to
}: {
  from: string
  to: string
}) => {
  const preselectedRange = getPreselectedDateRange({ from, to })

  // Should be number of days between dates in days
  const rangeValue = preselectedRange
    ? dayjs.duration(preselectedRange.value, preselectedRange.unit).as('days')
    : dayjs(to).diff(dayjs(from), 'days')
  const typeOfRange = preselectedRange ? 'preset' : 'custom'

  ReactGA.event({
    category: 'Activity history',
    action: 'Filter range',
    label: typeOfRange,
    value: rangeValue
  })
}

export const formatRowToCpdSelectedItem = (
  row: ActivityHistoryRowType
): CPDSelectedItem | null => {
  if (checkIsCPDEvidence(row)) {
    return null
  }

  const itemType: CpdBatchItemInput['itemType'] =
    row.__typename === ActivityHistoryTypeEnum.TopicEnrolmentModelAsStudent
      ? CpdUserEntryItemType.userTopic
      : CpdUserEntryItemType.userEvent

  return {
    itemType,
    itemId: row.id
  }
}

export const checkIsPageSelected = (
  selectedIds: number[],
  items?: ActivityHistoryQuery['me']['activityHistory']['items']
) => {
  if (!items || !selectedIds.length) return false

  return items.every((item) => {
    if (checkIsCPDEvidence(item)) return true
    return selectedIds.includes(item.id)
  })
}

export const getActivityRowsSelection = (
  items: ActivityHistoryRowType[],
  isChecked: boolean,
  selectedRowsData: ActivityTableSelectionState
): ActivityTableSelectionState => {
  const rowsData: ActivityTableSelectionState = {
    items: [],
    ids: []
  }

  for (let row of items) {
    const selectedItem = formatRowToCpdSelectedItem(row)

    if (
      !selectedItem ||
      (isChecked && selectedRowsData.ids.includes(selectedItem.itemId))
    )
      continue

    rowsData.items.push(selectedItem)
    rowsData.ids.push(row.id)
  }

  if (isChecked) {
    return {
      items: [...selectedRowsData.items, ...rowsData.items],
      ids: [...selectedRowsData.ids, ...rowsData.ids]
    }
  }

  return {
    items: selectedRowsData.items.filter(
      (item) => !rowsData.ids.includes(item.itemId)
    ),
    ids: selectedRowsData.ids.filter((id) => !rowsData.ids.includes(id))
  }
}

export const getCpdTextStatus = (row: ActivityHistoryRowType) => {
  const isCpdActivity = checkIsCPDEvidence(row) || row.isCpdEntry

  return isCpdActivity ? t`yes` : ''
}

export const getActivityFormattedDate = (date: string | undefined | null) => {
  if (!date) return ''

  const format = getDateFormatBasedOnLocale()

  return dayjs(date).format(format)
}
